import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { SPORTS, CITIES, SKILL_LEVELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Check, ArrowRight } from "lucide-react";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState(user?.username || (user?.name?.split(" ")[0] || ""));
  const [city, setCity] = useState(user?.city || "");
  const [sports, setSports] = useState(user?.sports || []);
  const [skillLevels, setSkillLevels] = useState(user?.skill_levels || {});
  const [saving, setSaving] = useState(false);

  const toggleSport = (id) => {
    setSports((prev) => {
      const next = prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id];
      if (!prev.includes(id) && !skillLevels[id]) {
        setSkillLevels((sl) => ({ ...sl, [id]: "intermediate" }));
      }
      return next;
    });
  };

  const setSkill = (sport, level) => setSkillLevels((sl) => ({ ...sl, [sport]: level }));

  const submit = async () => {
    if (!username.trim() || !city) {
      toast.error("Lengkapi data dulu ya");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me", {
        username: username.trim(),
        city,
        sports,
        skill_levels: skillLevels,
        onboarded: true,
      });
      setUser(data);
      toast.success("Profil siap! Yuk main.");
      navigate("/home", { replace: true });
    } catch (err) {
      console.warn("[onboarding] save failed:", err?.message);
      toast.error("Gagal simpan profil");
    } finally {
      setSaving(false);
    }
  };

  // Stable, non-"all" skill levels — memoized so this isn't recomputed per render
  const skillOptions = useMemo(() => SKILL_LEVELS.filter((l) => l.id !== "all"), []);

  return (
    <div className="min-h-screen bg-[#09090B] text-foreground">
      <div className="mx-auto max-w-md px-4 py-8">
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-primary" : "bg-secondary"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="fade-up">
            <h1 className="font-heading text-3xl font-black tracking-tight">Halo, {user?.name?.split(" ")[0]}! 👋</h1>
            <p className="mt-2 text-muted-foreground">Bikin username & pilih kota lo dulu.</p>

            <div className="mt-8 space-y-5">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Username</label>
                <input
                  data-testid="onboard-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="contoh: budi_main"
                  className="mt-2 w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 text-foreground placeholder-muted-foreground/60 focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kota</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {CITIES.map((c) => (
                    <button
                      key={c}
                      data-testid={`onboard-city-${c}`}
                      onClick={() => setCity(c)}
                      className={`text-sm font-medium px-3 py-3 rounded-2xl border transition-all ${city === c ? "bg-primary text-primary-foreground border-primary glow-cyan" : "bg-secondary text-foreground border-border hover:border-primary/40"}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              data-testid="onboard-next-1"
              onClick={() => username && city && setStep(2)}
              disabled={!username || !city}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-40 glow-cyan active:scale-95 transition-transform"
            >
              Lanjut <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="fade-up">
            <h1 className="font-heading text-3xl font-black tracking-tight">Olahraga apa aja?</h1>
            <p className="mt-2 text-muted-foreground">Pilih semua yang lo mainin.</p>

            <div className="mt-6 grid grid-cols-2 gap-2">
              {SPORTS.map((s) => {
                const active = sports.includes(s.id);
                return (
                  <button
                    key={s.id}
                    data-testid={`onboard-sport-${s.id}`}
                    onClick={() => toggleSport(s.id)}
                    className={`relative flex items-center gap-3 px-4 py-3.5 rounded-2xl border text-left transition-all ${active ? "border-primary bg-primary/10" : "border-border bg-secondary"}`}
                  >
                    <span className="text-2xl">{s.icon}</span>
                    <span className="font-bold text-sm">{s.label}</span>
                    {active && <Check className="absolute top-2 right-2 h-4 w-4 text-primary" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex gap-2">
              <button onClick={() => setStep(1)} className="flex-1 px-6 py-3.5 rounded-full bg-secondary border border-border font-bold">Balik</button>
              <button
                data-testid="onboard-next-2"
                onClick={() => sports.length > 0 && setStep(3)}
                disabled={sports.length === 0}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-40 glow-cyan"
              >
                Lanjut <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="fade-up">
            <h1 className="font-heading text-3xl font-black tracking-tight">Level skill lo?</h1>
            <p className="mt-2 text-muted-foreground">Buat matchmaking yang seimbang.</p>

            <div className="mt-6 space-y-3">
              {sports.map((sid) => {
                const s = SPORTS.find(x => x.id === sid);
                return (
                  <div key={sid} className="bg-card border border-border rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">{s?.icon}</span>
                      <span className="font-bold">{s?.label}</span>
                    </div>
                    <div className="flex gap-2">
                      {SKILL_LEVELS.filter(l => l.id !== "all").map((l) => (
                        <button
                          key={l.id}
                          data-testid={`skill-${sid}-${l.id}`}
                          onClick={() => setSkill(sid, l.id)}
                          className={`flex-1 px-3 py-2 rounded-full text-xs font-bold transition-all ${skillLevels[sid] === l.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground border border-border"}`}
                        >
                          {l.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex gap-2">
              <button onClick={() => setStep(2)} className="flex-1 px-6 py-3.5 rounded-full bg-secondary border border-border font-bold">Balik</button>
              <button
                data-testid="onboard-finish"
                onClick={submit}
                disabled={saving}
                className="flex-1 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-40 glow-cyan"
              >
                {saving ? "Menyimpan..." : "Mulai Main"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
